import type { 
    IContentLocation,
    IFetchNavigationItemReferencesResult,
    IReaderPublicationNavigation, 
    IReaderPublicationNavigationItem,
    IReaderPublicationNavigationItemReference,
    NavigationCollectionType, 
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import type { 
    ICirrusReaderNavigationCollection, 
    ICirrusReaderNavigationItem, 
    ICirrusReaderNavigationTreeData,
    ICirrusReaderReadingProgressionTimeline, 
} from "../shared/CirrusReaderModel";

/**
 * Define tree outside of the composable to maintain it as a singleton.
 */
const tree: ICirrusReaderNavigationTreeData = {
    collections: [],
    items: [],
};

export const useCirrusNavigationTree = (
    
) => {

    let _hasBeenBuilt: boolean = false;
    let _colibrioPublicationNavigation: IReaderPublicationNavigation | undefined;
    let _timeline: ICirrusReaderReadingProgressionTimeline | undefined;

    const fetchItemsInVisibleRange = async (
        types: NavigationCollectionType[],
        greedy: boolean = true,
        visibleRange: IContentLocation | null = null
    ): Promise<IReaderPublicationNavigationItemReference[] | undefined> => {
        const items: IFetchNavigationItemReferencesResult | undefined = await visibleRange?.fetchNavigationItemReferences({
            collectionTypes: types,
            greedy: greedy,
        });
        return items?.getItemsInRange();
    }

    const buildCirrusReaderNavigationTree = async (
        colibrioPublicationNavigation?: IReaderPublicationNavigation,
        timeline?: ICirrusReaderReadingProgressionTimeline,
    ): Promise<void>  => {
        _colibrioPublicationNavigation = colibrioPublicationNavigation;
        _timeline = timeline;
        
        if (_colibrioPublicationNavigation) {
            const collections = _colibrioPublicationNavigation.getNavigationCollections();
            for (const collection of collections) {
                const newCollection: ICirrusReaderNavigationCollection = {
                    type: collection.getType(),
                    children: [],
                };
                for (const item of collection.getChildren()) {
                    const navItem = await _createNavigationItem(item);
                    if (navItem) {
                        tree.items.push(navItem);
                        newCollection.children.push(navItem);
                    }
                }
                tree.collections.push(newCollection);
            }
            _hasBeenBuilt = true;
        }
    }

    const _createNavigationItem = async (navItem: IReaderPublicationNavigationItem): Promise<ICirrusReaderNavigationItem> => {
        const locator = navItem.getLocator();
        const textContent: string = navItem.getTextContent();
        let timelineStartPos: number | undefined;

        if (locator && _timeline) {
            timelineStartPos = await _timeline.fetchTimelinePosition(locator);
        }

        const newItem: ICirrusReaderNavigationItem = {
            locator: locator?.toString() || undefined,
            title: textContent,
            children: [],
            timelineStartPosition: timelineStartPos,
        };

        const children = navItem.getChildren();

        if (children && children.length > 0) {
            for (const item of children) {
                const newChildItem = await _createNavigationItem(item);
                newItem.children.push(newChildItem);
            }
        }

        return newItem;
    }

    return {
        buildCirrusReaderNavigationTree,
        fetchItemsInVisibleRange,
        treeData: computed(() => tree).value,
        hasBeenBuilt: computed(() => _hasBeenBuilt),
      }
}